// professionals.service.ts
import { Pagination } from '../interfaces/pagination';
import { http } from './http.service';
import { getUploadFileName } from '../utils/profilePicture';

// ---
export enum ProfessionalType {
  Lawyer = 1,
  Inspector = 2,
  Appraiser = 3,
  Developer = 4,
}

// ---
export enum ProfessionalSortBy {
    FirstName = 'firstName',
    LastName = 'lastName',
    Phone = 'phone',
    Email = 'email'
}

// Unified "Professional" interface matching UI requirements
export interface Professional {
  id: number;
  // UI often expects 'name', we can compose it
  name: string; 
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  website?: string;
  companyWebsite?: string;
  
  // Profession specifics
  professionType: number;
  professionName?: string; // Derived/mapped
  
  // Business details printed onto the generated client documents. All
  // optional, and they apply to every supplier type.
  companyName?: string;
  companyRegistrationNumber?: string;
  businessAddress?: string;
  authorizedSignatoryName?: string;
  authorizedSignatoryIdNumber?: string;
  bankDetails?: string;

  // Additional details
  licenseNumber?: string;
  shortDescription?: string;
  description?: string;
  isDisplayed: boolean;
  
  // Settlements
  settlementIds: number[];
  settlements?: any[]; // The generic objects returned by API
  
  profilePictureFileId?: number;
  profilePicture?: any; // Contains url and metadata
  
  // Legacy/UI compatibility fields (optional)
  contactPerson?: string; 
  address?: string; // Not in API payload?
  city?: string; // Not in API payload directly, maybe in settlements
  status?: 'מוצג' | 'מוסתר'; // from isDisplayed
}

export interface ProfessionalsResponse extends Pagination<Professional> {
  searchTerm?: string;
}

export interface ProfessionalPayload {
  professionalId?: number; // for update
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  companyName?: string | null;
  companyRegistrationNumber?: string | null;
  businessAddress?: string | null;
  authorizedSignatoryName?: string | null;
  authorizedSignatoryIdNumber?: string | null;
  bankDetails?: string | null;
  licenseNumber?: string | null;
  companyWebsite?: string | null;
  shortDescription?: string | null;
  description?: string | null;
  isDisplayed: boolean;
  professionType: number;
  settlements: number[];
}

interface ProfessionalsBusinessDetails {
  companyName?: string;
  companyRegistrationNumber?: string;
  businessAddress?: string;
  authorizedSignatoryName?: string;
  authorizedSignatoryIdNumber?: string;
  bankDetails?: string;
}

interface ProfessionalsApiItem extends ProfessionalsBusinessDetails {
  professionalId?: number; // In GET /Professionals response? or just "id"?
  // Usually GET /Professionals returns list of items. Let's assume standard "id" or "professionalId". 
  // GET /Professionals/{id} shows "professionalId" in path. 
  // Let's assume payload matches the CREATE/UPDATE fields mostly.
  id?: number;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  licenseNumber?: string;
  companyWebsite?: string;
  shortDescription?: string;
  description?: string;
  isDisplayed?: boolean;
  professionType?: number;
  profilePictureFileId?: number;
  profilePicture?: any;
  settlements?: any[]; // Array of ints or objects? "List of related settlements"
}

// Response wrapper if any
interface ProfessionalsApiResponse {
  items?: ProfessionalsApiItem[];
  data?: ProfessionalsApiItem[];
  // If array is root
  [key: number]: ProfessionalsApiItem;
}

const PROFESSIONALS_ENDPOINT = '/Professionals';

export const PROFESSION_LABELS: Record<number, string> = {
  [ProfessionalType.Lawyer]: 'עורך דין',
  [ProfessionalType.Inspector]: 'מפקח',
  [ProfessionalType.Appraiser]: 'שמאי',
  [ProfessionalType.Developer]: 'יזם',
};

// Start of Service Implementation

const normalizeProfessional = (item: ProfessionalsApiItem): Professional => {
    // Determine ID
    const id = item.id ?? item.professionalId ?? 0;
    
    // Names
    const firstName = item.firstName?.trim() || '';
    const lastName = item.lastName?.trim() || '';
    const name = [firstName, lastName].filter(Boolean).join(' ') || 'איש מקצוע';

    // Profession
    const professionType = item.professionType ?? 0;
    const professionName = PROFESSION_LABELS[professionType] || 'אחר';
    
    // Status
    const status = item.isDisplayed ? 'מוצג' : 'מוסתר';

    // Settlements
    // API might return array of objects or array of IDs. 
    // Screenshot: "settlements array integer | string".
    const rawSettlements = Array.isArray(item.settlements) ? item.settlements : [];
    const settlementIds = rawSettlements.map(s => (typeof s === 'object' ? s.id : Number(s))).filter(Boolean);

    return {
        id,
        name,
        firstName,
        lastName,
        email: item.email || '',
        phone: item.phone || '',
        companyWebsite: item.companyWebsite || '',
        website: item.companyWebsite || '',
        professionType,
        professionName,
        licenseNumber: item.licenseNumber || '',
        companyName: item.companyName || '',
        companyRegistrationNumber: item.companyRegistrationNumber || '',
        businessAddress: item.businessAddress || '',
        authorizedSignatoryName: item.authorizedSignatoryName || '',
        authorizedSignatoryIdNumber: item.authorizedSignatoryIdNumber || '',
        bankDetails: item.bankDetails || '',
        shortDescription: item.shortDescription || '',
        description: item.description || '',
        isDisplayed: !!item.isDisplayed,
        status,
        settlementIds,
        settlements: rawSettlements,
        profilePictureFileId: item.profilePictureFileId,
        profilePicture: item.profilePicture, // Add reference to the full picture object
        contactPerson: name // For UI compatibility
    };
};

const ProfessionalsService = {
  async getAllProfessionals(): Promise<ProfessionalsResponse> {
    try {
        // No pagination params passed to backend - fetch all
        // The screenshot simply shows GET /Professionals
        const response = await http.get<ProfessionalsApiResponse | ProfessionalsApiItem[]>(PROFESSIONALS_ENDPOINT);
        
        let rawItems: ProfessionalsApiItem[] = [];
        
        if (Array.isArray(response)) {
            rawItems = response;
        } else if (response && typeof response === 'object') {
             rawItems = (response as any).data || (response as any).items || [];
        }

        const items = rawItems.map(normalizeProfessional);
        const totalCount = items.length;

        // Return full list structure for client-side pagination
        return {
            items,
            totalCount,
            pageNumber: 1,
            pageSize: totalCount,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
            searchTerm: ''
        };

    } catch (error) {
        console.error('Failed to fetch professionals', error);
        throw error;
    }
  },

  async getProfessionStats(): Promise<Record<string, number>> {
      try {
          const response = await this.getAllProfessionals();
          const items = response.items;
          const stats: Record<string, number> = {};
          
          items.forEach(item => {
              const type = item.professionType;
              stats[type] = (stats[type] || 0) + 1;
          });
          
          return stats;
      } catch (error) {
          console.error('Failed to get stats', error);
          return {};
      }
  },

  async getProfessionalById(id: number): Promise<Professional> {
      try {
          // With the admin's token this is the admin shape: every field (signatory ID, bank details), hidden
          // professionals included.
          const response = await http.get<any>(`${PROFESSIONALS_ENDPOINT}/${id}`);
          
          let item = response;
          // Handle API response wrapper { meta: ..., data: ... }
          if (response && response.data && !response.id) {
              item = response.data;
          }
          
          return normalizeProfessional(item);
      } catch (error) {
          console.error(`Failed to fetch professional ${id}`, error);
          throw error;
      }
  },

  // POST /professionals/upload: 400 EXTENSION_NOT_ALLOWED / SINGLE_FILE / FILE_TOO_LARGE / UPLOAD_FAILED, 404
  // PROFESSIONAL_NOT_FOUND. The caller shows them (the form checks the picture first: utils/profilePicture).
  async uploadProfilePicture(professionalId: number, file: File) {
    // The server compares the extension case-sensitively: "IMG_0001.JPG" goes up as "IMG_0001.jpg".
    const fileName = getUploadFileName(file);
    const formData = new FormData();
    formData.append('file', file, fileName);
    formData.append('professionalId', String(professionalId));
    formData.append('title', fileName);

    // Response contains nested data: { meta: {}, data: { id: 8, identifier, url, ... } }
    const response = await http.post<any>(`${PROFESSIONALS_ENDPOINT}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      silentErrors: true
    });

    return response?.data ?? response;
  },

  async createProfessional(payload: ProfessionalPayload): Promise<Professional> {
      try {
          // POST /Professionals
          // Construct strict payload from whitelist to match API requirements exactly
          const creationPayload = {
              firstName: payload.firstName,
              lastName: payload.lastName,
              phone: payload.phone,
              email: payload.email,
              licenseNumber: payload.licenseNumber,
              companyWebsite: payload.companyWebsite,
              companyName: payload.companyName,
              companyRegistrationNumber: payload.companyRegistrationNumber,
              businessAddress: payload.businessAddress,
              authorizedSignatoryName: payload.authorizedSignatoryName,
              authorizedSignatoryIdNumber: payload.authorizedSignatoryIdNumber,
              bankDetails: payload.bankDetails,
              shortDescription: payload.shortDescription,
              description: payload.description,
              isDisplayed: payload.isDisplayed,
              professionType: payload.professionType,
              settlements: payload.settlements
          };
          
          // The form shows validation errors next to its fields (INVALID_EMAIL, INVALID_ID_NUMBER).
          const response = await http.post<ProfessionalsApiItem>(PROFESSIONALS_ENDPOINT, creationPayload, { silentErrors: true });
          
          let item: any = response;
          // Handle potential wrapper { data: ... }
          if (response && (response as any).data && !response.id && !response.professionalId) {
             item = (response as any).data;
          }

          if (item) {
             // Ensure ID is present for subsequent operations (like image upload)
             const professional = normalizeProfessional(item);
             if (!professional.id && (item.id || item.professionalId)) {
                professional.id = item.id || item.professionalId;
             }
             return professional;
          }

          // Fallback if response is empty but success (201 Created sometimes empty)
          // We must return a valid object, but ID might be missing. 
          // Ideally we should re-fetch if ID is missing but we don't have it.
          // Let's hope the API returns the created object.
          return normalizeProfessional({ ...payload, id: 0 } as any);
      } catch (error) {
          console.error('Failed to create professional', error);
          throw error;
      }
  },

  async updateProfessional(payload: ProfessionalPayload): Promise<void> {
      try {
          // PUT /Professionals, professionalId in the body. A field left out (undefined) or null keeps its value;
          // an empty string clears an optional one.
          const updatePayload = {
              professionalId: payload.professionalId,
              firstName: payload.firstName,
              lastName: payload.lastName,
              phone: payload.phone,
              email: payload.email,
              licenseNumber: payload.licenseNumber,
              companyWebsite: payload.companyWebsite,
              companyName: payload.companyName,
              companyRegistrationNumber: payload.companyRegistrationNumber,
              businessAddress: payload.businessAddress,
              authorizedSignatoryName: payload.authorizedSignatoryName,
              authorizedSignatoryIdNumber: payload.authorizedSignatoryIdNumber,
              bankDetails: payload.bankDetails,
              shortDescription: payload.shortDescription,
              description: payload.description,
              isDisplayed: payload.isDisplayed,
              professionType: payload.professionType,
              settlements: payload.settlements
          };
          await http.put(PROFESSIONALS_ENDPOINT, updatePayload, { silentErrors: true });
      } catch (error) {
          console.error('Failed to update professional', error);
          throw error;
      }
  },

  async deleteProfessional(id: number): Promise<void> {
      try {
          // DELETE /Professionals?professionalId=...
          // Use 'params' config for query parameters
          await http.delete(PROFESSIONALS_ENDPOINT, null, { params: { professionalId: id } });
      } catch (error) {
           console.error(`Failed to delete professional ${id}`, error);
           throw error;
      }
  }
};

export default ProfessionalsService;
